import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Phone, Clock, ChevronRight, ChevronLeft, Wrench, MapPin } from "lucide-react";
import { SITE, SERVICES } from "../lib/site";
import { ARTICLES } from "../lib/articleContent";
import { CITY_CONTENT } from "../lib/cityContent";

const BASE_URL = "https://alliancecraneservice.com";

const Block = ({ b }) => {
  if (b.type === "h2") return <h2 className="font-display text-2xl md:text-3xl mt-10 mb-4 leading-tight">{b.text}</h2>;
  if (b.type === "p") return <p className="text-zinc-300 leading-relaxed mb-5">{b.text}</p>;
  if (b.type === "ul") return (
    <ul className="space-y-2.5 mb-5">
      {b.items.map((it, i) => (
        <li key={i} className="flex items-start gap-3 text-zinc-300">
          <span className="w-1.5 h-1.5 bg-[var(--ac-red)] mt-2.5 shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
  if (b.type === "callout") return (
    <div className="my-8 border-l-2 border-[var(--ac-red)] bg-[var(--ac-bg-2)] p-5">
      <p className="text-white leading-relaxed">{b.text}</p>
    </div>
  );
  return null;
};

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = useMemo(() => ARTICLES[slug], [slug]);

  if (!article) return <Navigate to="/resources" replace />;

  const canonical = `${BASE_URL}/resources/${slug}`;

  const relServices = (article.relatedServices || []).map((s) => SERVICES.find((x) => x.slug === s)).filter(Boolean);
  const relCities = (article.relatedCities || []).map((c) => CITY_CONTENT[c]).filter(Boolean);
  const relArticles = (article.relatedArticles || []).map((a) => ARTICLES[a]).filter(Boolean);

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.metaDescription,
    image: article.image,
    author: { "@type": "Organization", name: SITE.name },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: { "@type": "ImageObject", url: SITE.logo },
    },
    mainEntityOfPage: canonical,
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Resources", item: `${BASE_URL}/resources` },
      { "@type": "ListItem", position: 3, name: article.title, item: canonical },
    ],
  };

  const faqLd = article.faqs && article.faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: article.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null;

  return (
    <>
      <Helmet>
        <title>{`${article.metaTitle} | ${SITE.shortName}`}</title>
        <meta name="description" content={article.metaDescription} />
        <meta name="keywords" content={article.keywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={article.metaTitle} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:image" content={article.image} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        {faqLd && <script type="application/ld+json">{JSON.stringify(faqLd)}</script>}
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[var(--ac-border)]">
        <div className="absolute inset-0">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/60" />
        </div>
        <div className="relative max-w-[900px] mx-auto px-5 md:px-8 pt-12 md:pt-20 pb-14 md:pb-20">
          <nav
            data-testid="breadcrumbs"
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 md:gap-2 text-[11px] md:text-xs uppercase tracking-[0.14em] md:tracking-[0.18em] font-bold text-zinc-400 mb-6 flex-wrap"
          >
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/resources" className="hover:text-white">Resources</Link>
          </nav>
          <div className="flex items-center gap-3 mb-5">
            <span className="kbd-badge">{article.category}</span>
            <span className="text-xs text-zinc-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {article.readTime}</span>
          </div>
          <h1
            data-testid="article-h1"
            className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.02]"
          >
            {article.title}
          </h1>
          <p className="mt-5 text-base md:text-lg text-zinc-300 leading-relaxed">{article.excerpt}</p>
        </div>
      </section>

      {/* BODY */}
      <article className="py-12 md:py-16">
        <div className="max-w-[900px] mx-auto px-5 md:px-8">
          {article.body.map((b, i) => <Block key={i} b={b} />)}

          {/* Inline CTA */}
          <div className="my-12 surface p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <div className="font-display text-2xl">Need a crane in Central Florida?</div>
              <p className="text-zinc-400 mt-1">Call for a same-day quote or request one online.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <a href={`tel:${SITE.phoneE164}`} className="btn-primary justify-center" data-testid="article-cta-call">
                <Phone className="w-4 h-4" /> {SITE.phone}
              </a>
              <Link to="/contact" className="btn-ghost justify-center" data-testid="article-cta-quote">
                Request Quote
              </Link>
            </div>
          </div>

          {/* FAQ */}
          {article.faqs && article.faqs.length > 0 && (
            <section className="mt-14">
              <div className="overline mb-5">FAQs</div>
              <div className="border-t border-[var(--ac-border)]">
                {article.faqs.map((f, i) => (
                  <details key={i} data-testid={`article-faq-${i}`} className="group border-b border-[var(--ac-border)] py-5">
                    <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                      <span className="font-display text-lg md:text-xl leading-snug">{f.q}</span>
                      <ChevronRight className="w-5 h-5 shrink-0 text-[var(--ac-red)] mt-1 transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="mt-3 text-zinc-400 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Related services & cities (topic cluster interlinks) */}
          <section className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
            {relServices.length > 0 && (
              <div>
                <div className="overline mb-4">Related Services</div>
                <ul className="space-y-2">
                  {relServices.map((s) => (
                    <li key={s.slug}>
                      <Link to={`/services/${s.slug}`} data-testid={`article-service-${s.slug}`} className="flex items-center gap-2 text-zinc-300 hover:text-[var(--ac-red)] transition-colors">
                        <Wrench className="w-4 h-4 text-[var(--ac-red)]" /> {s.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {relCities.length > 0 && (
              <div>
                <div className="overline mb-4">Serving These Areas</div>
                <ul className="space-y-2">
                  {relCities.map((c) => (
                    <li key={c.slug}>
                      <Link to={`/service-area/${c.slug}`} data-testid={`article-city-${c.slug}`} className="flex items-center gap-2 text-zinc-300 hover:text-[var(--ac-red)] transition-colors">
                        <MapPin className="w-4 h-4 text-[var(--ac-red)]" /> {c.name}, FL
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>
      </article>

      {/* Related articles */}
      {relArticles.length > 0 && (
        <section className="py-14 md:py-20 bg-[var(--ac-bg-2)] border-y border-[var(--ac-border)]">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="overline mb-3">Keep Reading</div>
                <h2 className="font-display text-3xl md:text-4xl">More guides.</h2>
              </div>
              <Link to="/resources" className="btn-ghost" data-testid="article-all-resources">
                <ChevronLeft className="w-4 h-4" /> All Resources
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-[var(--ac-border)]">
              {relArticles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/resources/${a.slug}`}
                  data-testid={`related-article-${a.slug}`}
                  className="hover-card-border border-b border-r border-[var(--ac-border)] p-7 group bg-[var(--ac-bg)]"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="kbd-badge">{a.category}</span>
                    <span className="text-[11px] text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {a.readTime}</span>
                  </div>
                  <h3 className="font-display text-2xl leading-tight group-hover:text-[var(--ac-red)] transition-colors">{a.title}</h3>
                  <p className="mt-3 text-sm text-zinc-400">{a.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
